// The guides — Sanity `post` documents in the `dubai` dataset (one document
// per language, linked by `translation.metadata`; root AGENTS.md, "Content —
// Sanity articles") — at /[lang]/guides and /[lang]/guides/[slug]. The site
// calls them guides; the CMS and @todo/core call them articles.
//
// Fetched once per export (prefetchGuides, from prefetchLists): every full
// article goes to the 'article' bucket for its page, and a card-sized copy of
// each locale's list to the list seed the hub, the home page and the "More
// guides" block bake; and each page's "Read also" cards (relatedGuidesSeedKey).

import type { Href } from 'expo-router';

import type { LocaleLinks } from '@todo/core/i18n/locale-links-context';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isSupportedLocale, type Locale } from '@todo/core/i18n/locales';
import { LIST_BUCKET, articlesIndexSeedKey } from '@todo/core/list-queries';
import { sanityClient } from '@todo/core/sanity/client';
import { allArticlesDetailQuery, type Article, type ArticleDetail } from '@todo/core/sanity/queries';
import { absoluteUrl, type Crumb, type HreflangLink } from '@todo/core/seo';
import { getPrefetched, setPrefetched } from '@todo/core/static-prefetch';

import { localePath } from './format';
import { pagesWithGuides } from './collections';
import { SITE } from '@site';

/** The section's path under the locale. */
export const GUIDES_PATH = 'guides';

/** Bucket of the full articles, keyed `${locale}:${slug}` (as on riviera). */
export const ARTICLE_BUCKET = 'article';

/**
 * A guide's language-neutral key: the English version's slug (the slugs are
 * localized). Pages name the guides they link to by it — the site's
 * collections and SITE_UI.hubGuides.
 */
export type GuideKey = string;

export function guidePath(slug: string): string {
  return `${GUIDES_PATH}/${slug}`;
}

/** The guides hub's breadcrumb entry (core's articlesCrumb points at riviera's /articles). */
export function guidesCrumb(locale: Locale): Crumb {
  return { name: SITE.crumbs.articles[locale], path: GUIDES_PATH };
}

export function articleKey(locale: Locale, slug: string): string {
  return `${locale}:${slug}`;
}

/** The fields of a card — what the list seed carries. */
function toCard(article: ArticleDetail): Article {
  return {
    _id: article._id,
    title: article.title,
    slug: article.slug,
    mainImage: article.mainImage,
    mainImageAlt: article.mainImageAlt,
    publishedAt: article.publishedAt,
    cities: article.cities,
    topics: article.topics,
  };
}

/** Every locale's articles, seeded for the guide pages. */
export async function prefetchGuides(): Promise<void> {
  await Promise.all(
    SUPPORTED_LOCALES.map(async (locale) => {
      const articles = await sanityClient.fetch<ArticleDetail[]>(allArticlesDetailQuery, { lang: locale });
      const withSlug = (articles ?? []).filter((a) => typeof a.slug === 'string' && a.slug);
      for (const article of withSlug) setPrefetched(ARTICLE_BUCKET, articleKey(locale, article.slug), article);
      setPrefetched(LIST_BUCKET, articlesIndexSeedKey(locale), withSlug.map(toCard));
    }),
  );
  await prefetchRelatedGuides();
}

/** Seed key of a page's "Read also" cards in one language (page: its path under the locale). */
export function relatedGuidesSeedKey(page: string, locale: Locale): string {
  return `related-guides:${page}:${locale}`;
}

/**
 * Each English article whose slug is one of `$keys` (GuideKey), with the
 * card of its `$lang` version: the English one itself, or the sibling of
 * that language in its translation.metadata — null when there's none.
 */
const RELATED_GUIDES_QUERY = `*[
  _type == "post"
  && language == "${DEFAULT_LOCALE}"
  && slug.current in $keys
  && !(_id in path("drafts.**"))
]{
  "key": slug.current,
  "card": select(
    $lang == "${DEFAULT_LOCALE}" => @,
    (*[_type == "translation.metadata" && references(^._id)][0].translations[].value->)[
      language == $lang && !(_id in path("drafts.**"))
    ][0]
  ){
    _id,
    title,
    "slug": slug.current,
    mainImage,
    "mainImageAlt": mainImage.alt,
    publishedAt,
    cities,
    topics
  }
}`;

/** The `locale` cards of `keys` that have a version in it, by key. */
async function fetchGuideCards(keys: readonly GuideKey[], locale: Locale): Promise<Map<string, Article>> {
  if (!keys.length) return new Map();
  const rows = await sanityClient.fetch<{ key: string; card: Article | null }[]>(RELATED_GUIDES_QUERY, {
    keys: [...keys],
    lang: locale,
  });
  const cards = new Map<string, Article>();
  for (const { key, card } of rows ?? []) if (card && typeof card.slug === 'string' && card.slug) cards.set(key, card);
  return cards;
}

/** `keys` in their order, without those `cards` has no version of. */
function inOrder(keys: readonly GuideKey[], cards: Map<string, Article>): Article[] {
  return keys.flatMap((key) => {
    const card = cards.get(key);
    return card ? [card] : [];
  });
}

/**
 * The cards of `keys` in `locale`, in the order given; an article with no
 * version in that language is left out (no link to a page that isn't there).
 * At build time for the seeds, in the browser when a page has none (a dev
 * server, or a client-side navigation, which swaps the baked head).
 */
export async function fetchRelatedGuides(keys: readonly GuideKey[], locale: Locale): Promise<Article[]> {
  return inOrder(keys, await fetchGuideCards(keys, locale));
}

/**
 * Seeds the "Read also" cards of every page that has them, in every language:
 * one query per language for every key, then each page's cards in its order.
 */
async function prefetchRelatedGuides(): Promise<void> {
  const pages = pagesWithGuides();
  const keys = [...new Set(pages.flatMap((p) => p.guides))];
  await Promise.all(
    SUPPORTED_LOCALES.map(async (locale) => {
      const cards = await fetchGuideCards(keys, locale);
      for (const { page, guides } of pages) {
        setPrefetched(LIST_BUCKET, relatedGuidesSeedKey(page, locale), inOrder(guides, cards));
      }
    }),
  );
}

/** The locale's articles as seeded at build time (newest first); empty on a dev server. */
export function seededGuides(locale: Locale): Article[] {
  return getPrefetched<Article[]>(LIST_BUCKET, articlesIndexSeedKey(locale)) ?? [];
}

/**
 * The article's versions that exist, its own first: from its translation
 * group, so each language links to that language's own slug — never this slug
 * under another prefix (docs/article-i18n-hreflang-findings.md).
 */
function versions(article: ArticleDetail, locale: Locale): { locale: Locale; slug: string }[] {
  const others = (article.translations ?? []).filter(
    (t): t is { locale: Locale; slug: string } => isSupportedLocale(t.locale) && t.locale !== locale && !!t.slug,
  );
  return [{ locale, slug: article.slug }, ...others];
}

/**
 * hreflang alternates: the versions that exist, x-default the English one.
 * Empty when the article has no other version (a lone self-link says nothing).
 */
export function guideAlternates(article: ArticleDetail, locale: Locale): HreflangLink[] {
  const all = versions(article, locale);
  if (all.length < 2) return [];
  const links = all.map((v) => ({ hrefLang: v.locale, href: absoluteUrl(v.locale, guidePath(v.slug)) }));
  const fallback = all.find((v) => v.locale === DEFAULT_LOCALE);
  return fallback ? [...links, { hrefLang: 'x-default', href: absoluteUrl(fallback.locale, guidePath(fallback.slug)) }] : links;
}

/**
 * The header's language links on an article page: each language's version, or
 * that language's guides hub when there is none.
 */
export function guideLocaleLinks(article: ArticleDetail, locale: Locale): LocaleLinks {
  const links: LocaleLinks = {};
  // Built at runtime, outside the typed-routes literal set (as in NavLink).
  for (const l of SUPPORTED_LOCALES) links[l] = localePath(l, GUIDES_PATH) as Href;
  for (const v of versions(article, locale)) links[v.locale] = localePath(v.locale, guidePath(v.slug)) as Href;
  return links;
}

/** Past this an alt text is almost certainly pasted body copy, not a description. */
const MAX_IMAGE_ALT_LENGTH = 125;

/** The main image's alt, or the title when it's missing or implausibly long (as on riviera). */
export function guideImageAlt(article: { title: string; mainImageAlt: string | null }): string {
  const alt = article.mainImageAlt?.trim();
  return alt && alt.length <= MAX_IMAGE_ALT_LENGTH ? alt : article.title;
}
